import React, { useState, useEffect, useContext } from 'react';
import '../../GridEditor/ItemBuildProgression.css';
import ItemBuildDisplay, { ItemBuildData } from './ItemBuildDisplay';
import ItemTooltip, { ItemDetails } from './ItemTooltip';
import ItemShop from '../../GridEditor/ItemShop';
import ItemDetailView from '../../GridEditor/ItemDetailView';
import GameAssetsContext from '../../../../XXX/context/GameAssetContext';

export interface ChampionBuildData {
  championName: string;
  itemBuild: ItemBuildData | null;
}

// Role type for quest system
export type Role = 'top' | 'jgl' | 'mid' | 'bot' | 'sup';


interface Props {
  primaryChampion: ChampionBuildData;
  opponentChampion?: ChampionBuildData;
  showBothChampions?: boolean;
  loading?: boolean;
  error?: string | null;
  className?: string;
  // Role quest props
  primaryRole?: Role;
  opponentRole?: Role;
  onPrimaryRoleChange?: (role: Role) => void;
  onOpponentRoleChange?: (role: Role) => void;
  // Callbacks for build changes (will trigger backend API calls)
  onBuildOrderChange?: (championName: string, buildOrder: { starter?: string; mainItems: string[] }) => void;
  onItemClick?: (itemId: number, itemName: string) => void;
  // Backend-provided effective build orders (source of truth, overrides local initialization)
  effectiveBuildOrders?: Record<string, { starter?: string; mainItems: string[] }>;
}

interface SelectedItemDetails {
  id: number;
  name: string;
}

interface ItemShopState {
  isOpen: boolean;
  championName: string;
}

const ItemBuildProgressionV2: React.FC<Props> = ({
  primaryChampion,
  opponentChampion,
  showBothChampions = false,
  loading = false,
  error = null,
  className = '',
  primaryRole = 'mid',
  opponentRole = 'mid',
  onPrimaryRoleChange,
  onOpponentRoleChange,
  onBuildOrderChange,
  onItemClick,
  effectiveBuildOrders
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  const [apiVersion, setApiVersion] = useState<string>("15.9.1");
  const [selectedItem, setSelectedItem] = useState<SelectedItemDetails | null>(null);
  const [itemShopState, setItemShopState] = useState<ItemShopState | null>(null);
  const [championBuildOrders, setChampionBuildOrders] = useState<Record<string, { starter?: string; mainItems: string[] }>>({});
  
  // Hover tooltip state
  const [hoveredItem, setHoveredItem] = useState<ItemDetails | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Apply backend-provided effective build orders (source of truth, overrides local initialization)
  useEffect(() => {
    if (effectiveBuildOrders && Object.keys(effectiveBuildOrders).length > 0) {
      console.log(`[ItemBuildProgressionV2] 🔄 Applying effective build orders from backend:`, effectiveBuildOrders);
      setChampionBuildOrders(prev => {
        const updated = { ...prev };
        for (const [champName, buildOrder] of Object.entries(effectiveBuildOrders)) {
          updated[champName] = buildOrder;
          console.log(`[ItemBuildProgressionV2] ✅ Set effective build for ${champName}: ${buildOrder.mainItems.length} items`);
        }
        return updated;
      });
    }
  }, [effectiveBuildOrders]);

  // Fetch the latest API version when component mounts
  useEffect(() => {
    const fetchApiVersion = async () => {
      try {
        const response = await fetch('https://ddragon.leagueoflegends.com/api/versions.json');
        if (response.ok) {
          const versions = await response.json();
          if (versions && versions.length > 0) {
            setApiVersion(versions[0]);
            console.log(`[ItemBuildProgressionV2] Using Data Dragon version: ${versions[0]}`);
          }
        }
      } catch (err) {
        console.warn('[ItemBuildProgressionV2] Error fetching API version, using default:', err);
      }
    };
    
    fetchApiVersion();
  }, []);

  // Support item constants
  const FINAL_SUPPORT_ITEM_IDS = new Set(['3869', '3870', '3871', '3876', '3877']);
  const ALL_SUPPORT_ITEM_IDS = new Set([
    '3865', '3866', '3867', // World Atlas, Runic Compass, Bounty of Worlds
    '3869', '3870', '3871', '3876', '3877' // Final support items
  ]);

  // Jungle starter item constants
  const JUNGLE_STARTER_IDS = new Set(['1101', '1102', '1103']);
  const DEFAULT_JUNGLE_STARTER = '1101'; // Scorchclaw Pup
  const DEFAULT_NON_JUNGLE_STARTER = '1055'; // Doran's Blade
  const isJungleStarterItem = (itemId: string): boolean => JUNGLE_STARTER_IDS.has(itemId);

  // Helper: get itemGroups for an item ID from gameWideAssets
  const getItemGroupsById = (itemId: string): string[] => {
    const item = gameWideAssets?.itemsDetails?.find(i => i.id === itemId || parseInt(i.id) === parseInt(itemId));
    return (item as any)?.itemGroups || [];
  };

  // Initialize default build orders from item build data
  useEffect(() => {
    const initializeDefaultBuilds = () => {
      const championsToInitialize = [primaryChampion];
      if (opponentChampion && opponentChampion.championName !== primaryChampion.championName) {
        championsToInitialize.push(opponentChampion);
      }
      
      championsToInitialize.forEach(championData => {
        const { championName, itemBuild } = championData;
        if (itemBuild && !championBuildOrders[championName]) {
          const champRole = championName === primaryChampion.championName ? primaryRole : opponentRole;
          const isSupport = champRole === 'sup';
          
          // Build items array: core items + boots (if present) + supplementary items
          // For support: skip intermediate support items, keep final support item
          const mainItems: string[] = [];
          const addedIds = new Set<string>();
          const ownedGroups = new Set<string>(); // Track item groups for conflict detection
          let hasBoots = false;
          let hasFinalSupportItem = false;

          // Helper to track groups when adding an item
          const trackGroups = (id: string) => {
            for (const group of getItemGroupsById(id)) {
              ownedGroups.add(group);
            }
          };

          // Helper to check for group conflict
          const checkGroupConflict = (id: string): string | null => {
            for (const group of getItemGroupsById(id)) {
              if (ownedGroups.has(group)) return group;
            }
            return null;
          };
          
          // Add core items (filtering support items for support role)
          const coreIds = [
            itemBuild.coreItems.item1_id.toString(),
            itemBuild.coreItems.item2_id.toString(),
            itemBuild.coreItems.item3_id.toString(),
          ];
          
          for (const id of coreIds) {
            if (addedIds.has(id)) continue;
            if (isSupport && ALL_SUPPORT_ITEM_IDS.has(id)) {
              if (FINAL_SUPPORT_ITEM_IDS.has(id)) {
                hasFinalSupportItem = true;
                mainItems.push(id);
                addedIds.add(id);
                trackGroups(id);
              }
              continue;
            }
            // Check item group conflict
            const conflict = checkGroupConflict(id);
            if (conflict) {
              console.log(`[ItemBuildProgressionV2] ⛔ Skipping core item ${id}: conflicts with group "${conflict}"`);
              continue;
            }
            mainItems.push(id);
            addedIds.add(id);
            trackGroups(id);
            if (isBootsItem(id)) hasBoots = true;
          }
          
          // Add boots if available and not already in core items
          if (itemBuild.boots?.item_id && !hasBoots) {
            const bootsId = itemBuild.boots.item_id.toString();
            if (!addedIds.has(bootsId)) {
              mainItems.push(bootsId);
              addedIds.add(bootsId);
              trackGroups(bootsId);
              hasBoots = true;
            }
          }
          
          // Bot quest allows 7 items, other roles 6
          const maxItems = champRole === 'bot' ? 7 : 6;
          
          // Add supplementary items to fill up slots (skip boots if we already have one)
          if (itemBuild.supplementaryItems) {
            for (const item of itemBuild.supplementaryItems) {
              const itemIdStr = item.item_id.toString();
              if (addedIds.has(itemIdStr)) continue;
              if (isBootsItem(itemIdStr) && hasBoots) continue;
              if (isSupport && ALL_SUPPORT_ITEM_IDS.has(itemIdStr)) {
                if (FINAL_SUPPORT_ITEM_IDS.has(itemIdStr) && !hasFinalSupportItem) {
                  hasFinalSupportItem = true;
                  mainItems.push(itemIdStr);
                  addedIds.add(itemIdStr);
                  trackGroups(itemIdStr);
                }
                continue;
              }
              // Check item group conflict
              const conflict = checkGroupConflict(itemIdStr);
              if (conflict) {
                console.log(`[ItemBuildProgressionV2] ⛔ Skipping supplementary item ${itemIdStr}: conflicts with group "${conflict}"`);
                continue;
              }
              
              if (mainItems.length < maxItems) {
                mainItems.push(itemIdStr);
                addedIds.add(itemIdStr);
                trackGroups(itemIdStr);
                if (isBootsItem(itemIdStr)) hasBoots = true;
              }
            }
          }
          
          // Support: ensure final support item is at position 0
          if (isSupport) {
            const finalIdx = mainItems.findIndex(id => FINAL_SUPPORT_ITEM_IDS.has(id));
            if (finalIdx > 0) {
              const [finalItem] = mainItems.splice(finalIdx, 1);
              mainItems.unshift(finalItem);
            } else if (finalIdx === -1) {
              // No final support item in core/supplementary — use the one from the API response
              const finalSupItemId = itemBuild.finalSupportItem?.item_id?.toString();
              if (finalSupItemId) {
                mainItems.unshift(finalSupItemId);
              }
            }
          }

          // Determine starter item based on role
          const isJungle = champRole === 'jgl';
          let starterId: string | undefined;

          if (isSupport) {
            starterId = '3865'; // World Atlas
          } else if (isJungle) {
            const dbStarter = itemBuild.starter?.item_id?.toString();
            if (dbStarter && isJungleStarterItem(dbStarter)) {
              starterId = dbStarter;
            } else {
              starterId = DEFAULT_JUNGLE_STARTER; // Scorchclaw Pup
              console.log(`[ItemBuildProgressionV2] 🌿 Jungle role: forcing starter to Scorchclaw Pup (${DEFAULT_JUNGLE_STARTER}), was: ${dbStarter}`);
            }
          } else {
            // Non-jungle, non-support role
            const dbStarter = itemBuild.starter?.item_id?.toString();
            if (dbStarter && isJungleStarterItem(dbStarter)) {
              starterId = DEFAULT_NON_JUNGLE_STARTER; // Doran's Blade
              console.log(`[ItemBuildProgressionV2] 🌿 Non-jungle role (${champRole}): replacing jungle starter ${dbStarter} with Doran's Blade (${DEFAULT_NON_JUNGLE_STARTER})`);
            } else {
              starterId = dbStarter;
            }
          }

          const defaultBuildOrder = {
            starter: starterId,
            mainItems: mainItems.slice(0, maxItems) // sup item + 5 others
          };
          
          setChampionBuildOrders(prev => ({
            ...prev,
            [championName]: defaultBuildOrder
          }));
        }
      });
    };
    
    if (primaryChampion.itemBuild || opponentChampion?.itemBuild) {
      initializeDefaultBuilds();
    }
  }, [primaryChampion, opponentChampion, championBuildOrders]);

  // Adjust build item count and starter when role changes (e.g. bot quest adds 7th item, jungle starter override)
  useEffect(() => {
    const adjustBuildForRole = (champData: { championName: string; itemBuild: any }, role: string) => {
      const { championName, itemBuild } = champData;
      const currentBuild = championBuildOrders[championName];
      if (!currentBuild || !itemBuild) return;

      const maxItems = role === 'bot' ? 7 : 6;
      let updated = false;
      let newStarter = currentBuild.starter;
      let newItems = [...currentBuild.mainItems];

      // --- Starter item override based on role ---
      const isJungle = role === 'jgl';
      const isSupport = role === 'sup';

      if (isSupport) {
        if (newStarter !== '3865') {
          newStarter = '3865';
          updated = true;
        }
      } else if (isJungle) {
        if (!newStarter || !isJungleStarterItem(newStarter)) {
          console.log(`[ItemBuildProgressionV2] 🌿 Role changed to jungle: forcing starter to Scorchclaw Pup (${DEFAULT_JUNGLE_STARTER}), was: ${newStarter}`);
          newStarter = DEFAULT_JUNGLE_STARTER;
          updated = true;
        }
      } else {
        // Non-jungle, non-support
        if (newStarter && isJungleStarterItem(newStarter)) {
          console.log(`[ItemBuildProgressionV2] 🌿 Role changed to ${role}: replacing jungle starter ${newStarter} with Doran's Blade (${DEFAULT_NON_JUNGLE_STARTER})`);
          newStarter = DEFAULT_NON_JUNGLE_STARTER;
          updated = true;
        }
      }

      // --- Adjust item count ---
      if (newItems.length < maxItems && itemBuild.supplementaryItems) {
        // Need to add items — find next available supplementary items
        const addedIds = new Set(newItems);
        if (newStarter) addedIds.add(newStarter);
        const hasBoots = newItems.some(id => isBootsItem(id));
        
        for (const item of itemBuild.supplementaryItems) {
          const itemIdStr = item.item_id.toString();
          if (addedIds.has(itemIdStr)) continue;
          if (isBootsItem(itemIdStr) && hasBoots) continue;
          if (newItems.length >= maxItems) break;
          newItems.push(itemIdStr);
          addedIds.add(itemIdStr);
        }
        
        if (newItems.length !== currentBuild.mainItems.length) {
          updated = true;
        }
      } else if (newItems.length > maxItems) {
        newItems = newItems.slice(0, maxItems);
        updated = true;
      }

      if (updated) {
        setChampionBuildOrders(prev => ({
          ...prev,
          [championName]: { starter: newStarter, mainItems: newItems }
        }));
      }
    };

    adjustBuildForRole(primaryChampion, primaryRole);
    if (opponentChampion) {
      adjustBuildForRole(opponentChampion, opponentRole);
    }
  }, [primaryRole, opponentRole]);

  // Handle build order changes
  const handleBuildOrderChange = (championName: string, buildOrder: { starter?: string; mainItems: string[] }) => {
    setChampionBuildOrders(prev => ({
      ...prev,
      [championName]: buildOrder
    }));
    console.log(`[ItemBuildProgressionV2] Updated build order for ${championName}:`, buildOrder);
    
    // Notify parent component (will trigger backend API call)
    if (onBuildOrderChange) {
      onBuildOrderChange(championName, { 
        starter: buildOrder.starter, 
        mainItems: buildOrder.mainItems
      });
    }
  };

  // Get item details from gameWideAssets
  const getItemDetails = (itemId: number): ItemDetails | null => {
    if (!gameWideAssets?.itemsDetails) return null;
    return gameWideAssets.itemsDetails.find(item => 
      item.id === itemId.toString() || parseInt(item.id) === itemId
    ) || null;
  };

  // Hover event handlers
  const handleItemMouseEnter = (itemId: number, itemName: string, event: React.MouseEvent) => {
    const itemDetails = getItemDetails(itemId);
    
    if (itemDetails) {
      setHoveredItem(itemDetails);
      setMousePosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleItemMouseMove = (event: React.MouseEvent) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleItemMouseLeave = () => {
    setHoveredItem(null);
  };

  // Handle item clicks
  const handleItemClick = (itemId: number, itemName: string) => {
    setSelectedItem({ id: itemId, name: itemName });
    if (onItemClick) {
      onItemClick(itemId, itemName);
    }
  };

  // Helper function to get item details by ID
  const getItemDetailsById = (itemId: string | number) => {
    const id = itemId.toString();
    return gameWideAssets?.itemsDetails?.find(item => 
      item.id === id || parseInt(item.id) === parseInt(id)
    );
  };

  // Boot item IDs
  const BOOTS_IDS = new Set([
    '1001', '3006', '3009', '3020', '3047', '3111', '3117', '3158', // T0/T1 boots
    '223009', '223020', '223047', '223111', '223158', '223006' // T2 boots
  ]);
  
  const isBootsItem = (itemId: string | number): boolean => {
    return BOOTS_IDS.has(itemId.toString());
  };
  
  // Helper function to get custom main items for a champion (if any custom build exists)
  const getCustomMainItems = (championName: string): string[] | undefined => {
    const buildOrder = championBuildOrders[championName];
    if (buildOrder?.mainItems && buildOrder.mainItems.length > 0) {
      return buildOrder.mainItems;
    }
    return undefined;
  };

  // Helper function to get the effective build order for a champion
  const getEffectiveBuildOrder = (championData: ChampionBuildData): ItemBuildData | null => {
    const { championName, itemBuild } = championData;
    const customBuildOrder = championBuildOrders[championName];
    
    if (!itemBuild) {
      return null;
    }
    
    // If no custom build order, return the original itemBuild but with boots integrated into supplementary
    if (!customBuildOrder) {
      const effectiveBuild = { ...itemBuild };
      
      // Check if core items already have boots
      const coreHasBoots = [
        effectiveBuild.coreItems.item1_id,
        effectiveBuild.coreItems.item2_id,
        effectiveBuild.coreItems.item3_id
      ].some(id => isBootsItem(id));
      
      // Filter supplementary items to exclude boots if core already has boots
      if (effectiveBuild.supplementaryItems) {
        let foundBootsInSupplementary = false;
        effectiveBuild.supplementaryItems = effectiveBuild.supplementaryItems.filter(item => {
          if (isBootsItem(item.item_id)) {
            if (coreHasBoots || foundBootsInSupplementary) {
              return false; // Skip duplicate boots
            }
            foundBootsInSupplementary = true;
          }
          return true;
        });
        
        // Add boots from boots field if no boots in core or supplementary
        if (!coreHasBoots && !foundBootsInSupplementary && effectiveBuild.boots) {
          effectiveBuild.supplementaryItems = [
            {
              item_id: effectiveBuild.boots.item_id,
              item_name: effectiveBuild.boots.name,
              most_common_slot: 4,
              slot_buys: effectiveBuild.boots.pick_count || 1,
              pct_in_that_slot: 50
            },
            ...effectiveBuild.supplementaryItems.slice(0, 2)
          ];
        }
      }
      
      effectiveBuild.boots = null;
      return effectiveBuild;
    }

    // Create a new build object using custom build order but with original item structure
    const effectiveBuild = { ...itemBuild };
    
    // Clear the boots field - custom builds have boots in mainItems already
    // This prevents ItemBuildDisplay from adding duplicate boots to optional items
    effectiveBuild.boots = null;
    
    // Update starter item
    if (customBuildOrder.starter) {
      const starterDetails = getItemDetailsById(customBuildOrder.starter);
      if (starterDetails) {
        effectiveBuild.starter = {
          item_id: parseInt(customBuildOrder.starter),
          name: starterDetails.name,
          pick_count: itemBuild.starter?.pick_count || 1
        };
      }
    } else {
      effectiveBuild.starter = null;
    }
    
    // Update core items (first 3 from main items)
    if (customBuildOrder.mainItems.length >= 3) {
      const [item1Id, item2Id, item3Id] = customBuildOrder.mainItems.slice(0, 3);
      const item1Details = getItemDetailsById(item1Id);
      const item2Details = getItemDetailsById(item2Id);
      const item3Details = getItemDetailsById(item3Id);
      
      effectiveBuild.coreItems = {
        item1_id: parseInt(item1Id),
        item1_name: item1Details?.name || 'Unknown Item',
        item2_id: parseInt(item2Id),
        item2_name: item2Details?.name || 'Unknown Item',
        item3_id: parseInt(item3Id),
        item3_name: item3Details?.name || 'Unknown Item',
        times_seen: itemBuild.coreItems.times_seen,
        pct_of_players: itemBuild.coreItems.pct_of_players
      };
    }
    
    // Update supplementary items (remaining from main items — can be 3 or 4 items for bot lane 7-item builds)
    const supplementaryItemIds = customBuildOrder.mainItems.slice(3);
    effectiveBuild.supplementaryItems = supplementaryItemIds.map((itemId, index) => {
      const itemDetails = getItemDetailsById(itemId);
      const originalSupplementary = itemBuild.supplementaryItems[index];
      
      return {
        item_id: parseInt(itemId),
        item_name: itemDetails?.name || 'Unknown Item',
        most_common_slot: originalSupplementary?.most_common_slot || (index + 4),
        slot_buys: originalSupplementary?.slot_buys || 1,
        pct_in_that_slot: originalSupplementary?.pct_in_that_slot || 50
      };
    });
    
    return effectiveBuild;
  };

  if (loading) {
    return (
      <div className={`item-build-progression ${className} ${showBothChampions ? 'dual-champion' : ''}`}>
        <div className="loading-message">
          <div className="loading-spinner"></div>
          <span>Loading champion builds...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`item-build-progression ${className} ${showBothChampions ? 'dual-champion' : ''}`}>
        <div className="error-message">
          <span>Error loading champion builds: {error}</span>
        </div>
      </div>
    );
  }

  // If showing both champions
  if (showBothChampions && opponentChampion && opponentChampion.championName !== primaryChampion.championName) {
    const primaryEffectiveBuild = getEffectiveBuildOrder(primaryChampion);
    const opponentEffectiveBuild = getEffectiveBuildOrder(opponentChampion);

    return (
      <div className={`item-build-progression dual-champion ${className}`}>
        <div className="dual-champion-container">
          {primaryEffectiveBuild ? (
            <ItemBuildDisplay
              championName={primaryChampion.championName}
              itemBuild={primaryEffectiveBuild}
              teamColor="blue"
              apiVersion={apiVersion}
              onItemClick={handleItemClick}
              onItemMouseEnter={handleItemMouseEnter}
              onItemMouseMove={handleItemMouseMove}
              onItemMouseLeave={handleItemMouseLeave}
              onChangeItemBuild={() => setItemShopState({ isOpen: true, championName: primaryChampion.championName })}
              showChangeButton={true}
              role={primaryRole}
              customMainItems={getCustomMainItems(primaryChampion.championName)}
            />
          ) : (
            <div className="no-build-message blue-champion">
              <div className="champion-name">{primaryChampion.championName}</div>
              <div>No build data available</div>
            </div>
          )}

          {opponentEffectiveBuild ? (
            <ItemBuildDisplay
              championName={opponentChampion.championName}
              itemBuild={opponentEffectiveBuild}
              teamColor="red"
              apiVersion={apiVersion}
              onItemClick={handleItemClick}
              onItemMouseEnter={handleItemMouseEnter}
              onItemMouseMove={handleItemMouseMove}
              onItemMouseLeave={handleItemMouseLeave}
              onChangeItemBuild={() => setItemShopState({ isOpen: true, championName: opponentChampion.championName })}
              showChangeButton={true}
              role={opponentRole}
              customMainItems={getCustomMainItems(opponentChampion.championName)}
            />
          ) : (
            <div className="no-build-message red-champion">
              <div className="champion-name">{opponentChampion.championName}</div>
              <div>No build data available</div>
            </div>
          )}
        </div>
        
        {/* Item Detail View */}
        {selectedItem && (
          <>
            <div className="item-detail-divider"></div>
            <ItemDetailView
              itemId={selectedItem.id}
              itemName={selectedItem.name}
              apiVersion={apiVersion}
              onClose={() => setSelectedItem(null)}
              className="item-detail-view-container"
            />
          </>
        )}
        
        {/* Item Shop */}
        {itemShopState && (
          <>
            <div className="item-detail-divider"></div>
            <ItemShop
              isOpen={itemShopState.isOpen}
              championName={itemShopState.championName}
              onClose={() => setItemShopState(null)}
              onItemSelect={(itemId, itemName) => {
                console.log(`[ItemBuildProgressionV2] Selected item ${itemName} (${itemId}) for ${itemShopState.championName}`);
              }}
              currentBuildOrder={championBuildOrders[itemShopState.championName] || { starter: undefined, mainItems: [] }}
              onBuildOrderChange={handleBuildOrderChange}
              className="item-shop-container"
              role={itemShopState.championName === primaryChampion.championName ? primaryRole : opponentRole}
              onRoleChange={itemShopState.championName === primaryChampion.championName ? onPrimaryRoleChange : onOpponentRoleChange}
            />
          </>
        )}
      </div>
    );
  }

  // Single champion display
  const effectiveBuild = getEffectiveBuildOrder(primaryChampion);

  if (!effectiveBuild) {
    return (
      <div className={`item-build-progression ${className}`}>
        <div className="no-build-message">
          No item build available for {primaryChampion.championName}
        </div>
      </div>
    );
  }

  return (
    <div className={`item-build-progression ${className}`}>
      {/* Section Header and Description */}
      <div className="ge-section-header">
        <h4>Build Order</h4>
      </div>
      <div className="ge-section-description">
        <p>Adjust the build that will be set as the default for the wave progression table below.</p>
        <p>This can still be adjusted for individual waves later - it's not final, but sets the baseline progression.</p>
      </div>
      
      <div className="progression-header blue-team">
        <h4>Item Build for {primaryChampion.championName}</h4>
      </div>
      
      <ItemBuildDisplay
        championName={primaryChampion.championName}
        itemBuild={effectiveBuild}
        teamColor="blue"
        apiVersion={apiVersion}
        onItemClick={handleItemClick}
        onItemMouseEnter={handleItemMouseEnter}
        onItemMouseMove={handleItemMouseMove}
        onItemMouseLeave={handleItemMouseLeave}
        onChangeItemBuild={() => setItemShopState({ isOpen: true, championName: primaryChampion.championName })}
        showChangeButton={true}
        role={primaryRole}
        customMainItems={getCustomMainItems(primaryChampion.championName)}
      />
      
      {/* Item Detail View */}
      {selectedItem && (
        <>
          <div className="item-detail-divider"></div>
          <ItemDetailView
            itemId={selectedItem.id}
            itemName={selectedItem.name}
            apiVersion={apiVersion}
            onClose={() => setSelectedItem(null)}
            className="item-detail-view-container"
          />
        </>
      )}
      
      {/* Item Shop */}
      {itemShopState && (
        <>
          <div className="item-detail-divider"></div>
          <ItemShop
            isOpen={itemShopState.isOpen}
            championName={itemShopState.championName}
            onClose={() => setItemShopState(null)}
            onItemSelect={(itemId, itemName) => {
              console.log(`[ItemBuildProgressionV2] Selected item ${itemName} (${itemId}) for ${itemShopState.championName}`);
            }}
            currentBuildOrder={championBuildOrders[itemShopState.championName] || { starter: undefined, mainItems: [] }}
            onBuildOrderChange={handleBuildOrderChange}
            className="item-shop-container"
            role={primaryRole}
            onRoleChange={onPrimaryRoleChange}
          />
        </>
      )}
      
      {/* Enhanced Tooltip */}
      {hoveredItem && (
        <ItemTooltip
          item={hoveredItem}
          mousePosition={mousePosition}
          apiVersion={apiVersion}
        />
      )}
    </div>
  );
};

export default ItemBuildProgressionV2;
